import { useCallback, useEffect, useMemo, useRef } from 'react';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import Box from '@mui/material/Box';

import { useKaapAzureToken } from '../../hooks';
import { ApplicationSection } from './sections/ApplicationSection';
import { CabInformSection } from './sections/CabInformSection';
import { ClusterSection } from './sections/ClusterSection';
import { GitOpsSection } from './sections/GitOpsSection';
import { OwnerSection } from './sections/OwnerSection';
import { PimSection } from './sections/PimSection';
import { ResourcesSection, ResourcesValue } from './sections/ResourcesSection';
import { SecurityGroupsSection } from './sections/SecurityGroupsSection';
import { SlackChannelSection } from './sections/SlackChannelSection';
import { StageSection } from './sections/StageSection';
import { TenantNameSection } from './sections/TenantNameSection';
import {
  DEFAULT_DATA,
  KaapClusterNodeGroupValue,
  KaapGitOpsConfigValue,
  KaapTenantFormData,
  KaapTenantFormUiOptions,
} from './types';
import { getTenantNameMaxLength } from './validation';

export const KaapTenantForm = (
  props: FieldExtensionComponentProps<
    KaapTenantFormData,
    KaapTenantFormUiOptions
  >,
) => {
  const { onChange, formData, schema, uiSchema } = props;

  useKaapAzureToken();

  const mode = uiSchema?.['ui:options']?.mode ?? 'create';
  const isEdit = mode === 'edit';
  const allowMultiClusterSelection =
    uiSchema?.['ui:options']?.allowMultiClusterSelection ?? false;

  const data = useMemo<KaapTenantFormData>(
    () => ({ ...DEFAULT_DATA, ...formData }),
    [formData],
  );

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => {
    onChangeRef.current({ ...DEFAULT_DATA, ...formData });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateData = useCallback((patch: Partial<KaapTenantFormData>) => {
    onChangeRef.current({ ...dataRef.current, ...patch });
  }, []);

  const handleStageChange = useCallback(
    (stage: string) => updateData({ stage }),
    [updateData],
  );
  const handleTenantNameChange = useCallback(
    (tenantName: string) => updateData({ tenantName }),
    [updateData],
  );
  const handleOwnerChange = useCallback(
    (owner: string) => updateData({ owner }),
    [updateData],
  );
  const handleSecurityGroupsChange = useCallback(
    (securityObjectIds: string[]) => updateData({ securityObjectIds }),
    [updateData],
  );
  const handlePimChange = useCallback(
    (pimEnabled: boolean) => updateData({ pimEnabled }),
    [updateData],
  );
  const handleApplicationChange = useCallback(
    (application: string) => updateData({ application }),
    [updateData],
  );
  const handleSlackChange = useCallback(
    (slackChannel: string) => updateData({ slackChannel }),
    [updateData],
  );
  const handleClusterChange = useCallback(
    (clusterSelection: KaapClusterNodeGroupValue) =>
      updateData({ clusterSelection }),
    [updateData],
  );
  const handleCabInformChange = useCallback(
    (cabInform: boolean) => updateData({ cabInform }),
    [updateData],
  );
  const handleResourcesChange = useCallback(
    (patch: Partial<ResourcesValue>) => updateData(patch),
    [updateData],
  );
  const handleGitOpsChange = useCallback(
    (gitopsConfig: KaapGitOpsConfigValue) => updateData({ gitopsConfig }),
    [updateData],
  );

  const resourcesValue = useMemo<ResourcesValue>(
    () => ({
      namespaceQuota: data.namespaceQuota,
      cpuRequest: data.cpuRequest,
      cpuLimit: data.cpuLimit,
      memoryRequest: data.memoryRequest,
      memoryLimit: data.memoryLimit,
    }),
    [
      data.namespaceQuota,
      data.cpuRequest,
      data.cpuLimit,
      data.memoryRequest,
      data.memoryLimit,
    ],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} style={{ gap: 24 }}>
      <StageSection
        value={data.stage}
        onChange={handleStageChange}
        disabled={isEdit}
      />
      <TenantNameSection
        value={data.tenantName}
        stage={data.stage}
        tenantId={data.tenantId}
        maxLength={getTenantNameMaxLength(schema)}
        onChange={handleTenantNameChange}
        disabled={isEdit}
      />
      <OwnerSection
        value={data.owner}
        onChange={handleOwnerChange}
        disabled={isEdit}
      />
      <SecurityGroupsSection
        value={data.securityObjectIds}
        onChange={handleSecurityGroupsChange}
      />
      <PimSection value={data.pimEnabled} onChange={handlePimChange} />
      <ApplicationSection
        value={data.application}
        onChange={handleApplicationChange}
        disabled={isEdit}
      />
      <SlackChannelSection
        value={data.slackChannel}
        label={isEdit ? 'Slack channel name' : 'Slack Channel Name'}
        onChange={handleSlackChange}
      />
      <ClusterSection
        value={data.clusterSelection}
        stage={data.stage}
        onChange={handleClusterChange}
        disabled={isEdit}
        multiple={allowMultiClusterSelection}
      />
      <CabInformSection
        value={data.cabInform}
        tier={data.clusterSelection?.tier}
        onChange={handleCabInformChange}
      />
      <ResourcesSection
        value={resourcesValue}
        onChange={handleResourcesChange}
      />
      {isEdit && (
        <GitOpsSection
          value={data.gitopsConfig}
          onChange={handleGitOpsChange}
        />
      )}
    </Box>
  );
};
